/**
 * Арифметика зума доски.
 *
 * Отдельным файлом от хука, потому что проверять надо именно её: жесты
 * в тестах не воспроизвести, а ошибка в пересчёте прокрутки выглядит как
 * «доска уезжает из-под пальцев» и глазами ловится плохо.
 */

/**
 * Дальше приближать некуда.
 *
 * Доска — 25 клеток плюс поля, то есть 26 единиц viewBox. На телефоне шириной
 * 360 это 13 пикселей на клетку, и втрое — 40, ровно рекомендованный минимум
 * для касания. Больше смысла нет: на экран перестаёт попадать даже свой двор
 * целиком, а доску приходится возить постоянно.
 */
export const MAX_ZOOM = 3

/** Меньше единицы не уменьшаем: доска целиком и есть исходный вид. */
export const MIN_ZOOM = 1

/** Где мы сейчас: во сколько раз приближено и куда сдвинуто. */
export type ZoomState = {
  zoom: number
  /**
   * Сдвиг в долях прокручиваемого запаса, а не в пикселях.
   *
   * Пиксели привязаны к текущему размеру окна, и после поворота экрана
   * увели бы доску за край. Доля же означает одно и то же при любом размере —
   * а размер меняется и от поворота, и от появления адресной строки.
   */
  atX: number
  atY: number
}

export const AT_CENTRE: ZoomState = { zoom: MIN_ZOOM, atX: 0.5, atY: 0.5 }

/**
 * Масштаб в допустимых границах.
 *
 * Нечисло и бесконечность сбрасываются к целой доске, а не к потолку: и то и
 * другое означает испорченное вычисление, а из максимального приближения
 * игрок не поймёт, где он оказался.
 */
export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM

  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
}

/** Расстояние между двумя касаниями — им и меряется щипок. */
export function spread(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/**
 * В какой точке доски палец — долей от всей её ширины (или высоты).
 *
 * Доля, а не «во сколько раз изменился масштаб». Пересчёт через отношение
 * масштабов верен только тогда, когда сама область просмотра не меняется, — а
 * она меняется: при входе в приближение окно из блока в потоке становится
 * полноэкранным, и содержимое скачком выросло бы в разы от одного микрощипка.
 * Доля же остаётся той же при любом размере окна.
 *
 * @param  at       где сейчас прокрутка, пиксели
 * @param  point    палец в координатах видимой части, пиксели
 * @param  content  вся ширина (высота) содержимого, пиксели
 */
export function pointShare(at: number, point: number, content: number): number {
  if (content <= 0) return 0.5

  return (at + point) / content
}

/**
 * Куда прокрутить, чтобы точка под пальцами осталась под ними же.
 *
 * Считается по УЖЕ применённым размерам: масштаб доски и размер окна к этому
 * моменту новые, и промежуточных прогнозов не нужно. Без такого пересчёта
 * щипок «в угол» уводит доску — масштаб растёт от левого верхнего края, и всё,
 * на что смотрел игрок, уползает тем сильнее, чем ближе приближение.
 *
 * @param  share    доля из pointShare
 * @param  point    палец в координатах видимой части, пиксели
 * @param  content  новая ширина (высота) содержимого, пиксели
 */
export function keepUnderFingers(share: number, point: number, content: number): number {
  return share * content - point
}

/** Прокрутка не дальше края: за содержимым пусто, и упираться в пустоту незачем. */
export function clampScroll(at: number, room: number): number {
  if (room <= 0) return 0

  return Math.min(room, Math.max(0, at))
}

/** Доля от запаса прокрутки — в ней и держится положение. */
export function toShare(at: number, room: number): number {
  if (room <= 0) return 0.5

  return clampScroll(at, room) / room
}

/** Обратно: доля в пиксели прокрутки. */
export function fromShare(share: number, room: number): number {
  if (room <= 0) return 0
  if (!Number.isFinite(share)) return 0

  return clampScroll(share * room, room)
}
